// API Configuration for connecting to Node.js backend
import { API_BASE_URL } from "@/utils/constants";
import { showError, showSuccess } from "@/utils/dialog";

// Use sessionStorage so each browser tab has its own login (different roles per tab)
const AUTH_TOKEN_KEY = "token";
const getAuthToken = () => {
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
};

export const setAuthToken = (token) => {
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
};

export const removeAuthToken = () => {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
};

const getSuccessMessageForRequest = (method, endpoint) => {
  const key = `${String(method || "").toUpperCase()} ${String(endpoint || "").toLowerCase()}`;
  const exact = {
    "POST /payments": "Payment recorded successfully.",
    "POST /payments/refund": "Refund recorded successfully.",
    "POST /workflow-tickets": "Ticket created successfully.",
    "PUT /workflow-tickets/:id/accept": "Ticket accepted successfully.",
    "PUT /workflow-tickets/:id/complete": "Ticket completed successfully.",
    "PUT /workflow-tickets/:id/status": "Ticket status updated successfully.",
    "POST /invoices/:id/approve": "Invoice approved successfully.",
    "POST /orders/:id/close": "Order closed and invoice created successfully.",
  };
  if (exact[key]) return exact[key];

  const rules = [
    { pattern: /^POST \/payments$/, message: "Payment recorded successfully." },
    { pattern: /^POST \/payments\/refund$/, message: "Refund recorded successfully." },
    { pattern: /^POST \/workflow-tickets$/, message: "Ticket created successfully." },
    { pattern: /^PUT \/workflow-tickets\/[^/]+\/accept$/, message: "Ticket accepted successfully." },
    { pattern: /^PUT \/workflow-tickets\/[^/]+\/complete$/, message: "Ticket completed successfully." },
    { pattern: /^PUT \/workflow-tickets\/[^/]+\/status$/, message: "Ticket status updated successfully." },
    { pattern: /^POST \/invoices\/[^/]+\/approve$/, message: "Invoice approved successfully." },
    { pattern: /^POST \/orders\/[^/]+\/close$/, message: "Order closed and invoice created successfully." },
    { pattern: /^POST \//, message: "Created successfully." },
    { pattern: /^PUT \//, message: "Updated successfully." },
    { pattern: /^PATCH \//, message: "Updated successfully." },
    { pattern: /^DELETE \//, message: "Deleted successfully." },
  ];
  const match = rules.find((r) => r.pattern.test(key));
  return match ? match.message : "Action completed successfully.";
};

// Base fetch function with authentication
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}${endpoint}`;
  const method = String(options.method || "GET").toUpperCase();
  const shouldShowStatusPopup =
    options.showStatusPopup !== false && method !== "GET";

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const { showStatusPopup, ...fetchOptions } = options;
  const config = { ...fetchOptions, headers };

  try {
    const response = await fetch(url, config);

    // Handle non-JSON responses (like PDF/Excel exports)
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();

      if (!response.ok) {
        if (shouldShowStatusPopup) {
          const message =
            data.detail || data.message || `HTTP error! status: ${response.status}`;
          showError(message, "Action failed").catch(() => {});
        }
        throw new Error(
          data.detail || `HTTP error! status: ${response.status}`
        );
      }

      if (shouldShowStatusPopup) {
        showSuccess(
          data.message || getSuccessMessageForRequest(method, endpoint),
          "Success",
        ).catch(() => {});
      }
      return data;
    } else {
      // Return blob for file downloads
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        if (shouldShowStatusPopup) {
          showError(
            error.detail || `HTTP error! status: ${response.status}`,
            "Action failed",
          ).catch(() => {});
        }
        throw new Error(
          error.detail || `HTTP error! status: ${response.status}`
        );
      }
      if (shouldShowStatusPopup) {
        showSuccess(getSuccessMessageForRequest(method, endpoint), "Success").catch(() => {});
      }
      return response;
    }
  } catch (error) {
    console.error("API request error:", error);

    // Handle network errors
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      const message = "Network error. Please check your connection.";
      if (shouldShowStatusPopup) {
        showError(message, "Action failed").catch(() => {});
      }
      throw new Error(message);
    }

    // Handle 401 errors (unauthorized) - redirect to login
    if (
      error.message?.includes("401") ||
      error.message?.includes("Unauthorized") ||
      error.message?.includes("token")
    ) {
      removeAuthToken();
      if (
        window.location.pathname !== "/login" &&
        window.location.pathname !== "/"
      ) {
        window.location.href = "/login";
      }
      const message = "Session expired. Please login again.";
      if (shouldShowStatusPopup) {
        showError(message, "Action failed").catch(() => {});
      }
      throw new Error(message);
    }

    // Handle 403 errors (forbidden)
    if (
      error.message?.includes("403") ||
      error.message?.includes("Forbidden")
    ) {
      const message = "You don't have permission to perform this action.";
      if (shouldShowStatusPopup) {
        showError(message, "Action failed").catch(() => {});
      }
      throw new Error(message);
    }

    // Handle 404 errors
    if (
      error.message?.includes("404") ||
      error.message?.includes("Not found")
    ) {
      const message = "Resource not found.";
      if (shouldShowStatusPopup) {
        showError(message, "Action failed").catch(() => {});
      }
      throw new Error(message);
    }

    // Re-throw with original message
    throw error;
  }
};

// API methods
export const api = {
  // Auth endpoints
  auth: {
    login: (email, password) =>
      apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),

    changePassword: (oldPassword, newPassword) =>
      apiRequest("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      }),

    getMe: () => apiRequest("/auth/me"),
  },

  // Users endpoints
  users: {
    getAll: () => apiRequest("/users"),
    create: (userData) =>
      apiRequest("/users", {
        method: "POST",
        body: JSON.stringify(userData),
      }),
    update: (userId, userData) =>
      apiRequest(`/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify(userData),
      }),
    delete: (userId) =>
      apiRequest(`/users/${userId}`, {
        method: "DELETE",
      }),
    resetPassword: (userId, newPassword) =>
      apiRequest("/users/reset-password", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, new_password: newPassword }),
      }),
    getProgress: (userId, filters = {}) => {
      const params = new URLSearchParams();
      if (filters.range) params.append("range", filters.range);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
      const q = params.toString();
      return apiRequest(`/users/${userId}/progress${q ? `?${q}` : ""}`);
    },
    checkEmail: (email) =>
      apiRequest(`/users/check-email/${encodeURIComponent(email)}`),
  },

  // Customers endpoints
  customers: {
    getAll: () => apiRequest("/customers"),
    getById: (customerId) => apiRequest(`/customers/${customerId}`),
    create: (customerData) =>
      apiRequest("/customers", {
        method: "POST",
        body: JSON.stringify(customerData),
      }),
    update: (customerId, customerData) =>
      apiRequest(`/customers/${customerId}`, {
        method: "PUT",
        body: JSON.stringify(customerData),
      }),
    delete: (customerId) =>
      apiRequest(`/customers/${customerId}`, {
        method: "DELETE",
      }),
  },

  // Vendors endpoints
  vendors: {
    getAll: (category = null) => {
      const q = category ? `?category=${encodeURIComponent(category)}` : "";
      return apiRequest(`/vendors${q}`);
    },
    getById: (vendorId) => apiRequest(`/vendors/${vendorId}`),
    create: (vendorData) =>
      apiRequest("/vendors", {
        method: "POST",
        body: JSON.stringify(vendorData),
      }),
    update: (vendorId, vendorData) =>
      apiRequest(`/vendors/${vendorId}`, {
        method: "PUT",
        body: JSON.stringify(vendorData),
      }),
    delete: (vendorId) =>
      apiRequest(`/vendors/${vendorId}`, {
        method: "DELETE",
      }),
  },

  // Leads endpoints
  leads: {
    getAll: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.stage) params.append("stage", filters.stage);
      if (filters.assigned_to) params.append("assigned_to", filters.assigned_to);
      const q = params.toString();
      return apiRequest(`/leads${q ? `?${q}` : ""}`);
    },
    getById: (leadId) => apiRequest(`/leads/${leadId}`),
    create: (leadData) =>
      apiRequest("/leads", {
        method: "POST",
        body: JSON.stringify(leadData),
      }),
    update: (leadId, leadData) =>
      apiRequest(`/leads/${leadId}`, {
        method: "PUT",
        body: JSON.stringify(leadData),
      }),
    assign: (leadId, assignedTo) =>
      apiRequest(`/leads/${leadId}/assign`, {
        method: "PUT",
        body: JSON.stringify({ assigned_to: assignedTo }),
      }),
    bulkCreate: (leadsList) =>
      apiRequest("/leads/bulk", {
        method: "POST",
        body: JSON.stringify({ leads: leadsList }),
      }),
    delete: (leadId) =>
      apiRequest(`/leads/${leadId}`, {
        method: "DELETE",
      }),
  },

  // Orders endpoints
  orders: {
    getAll: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.customer_id) params.append("customer_id", filters.customer_id);
      const q = params.toString();
      return apiRequest(`/orders${q ? `?${q}` : ""}`);
    },
    getById: (orderId) => apiRequest(`/orders/${orderId}`),
    getServiceTypes: () => apiRequest("/orders/service-types"),
    create: (orderData) =>
      apiRequest("/orders", {
        method: "POST",
        body: JSON.stringify(orderData),
      }),
    update: (orderId, orderData) =>
      apiRequest(`/orders/${orderId}`, {
        method: "PUT",
        body: JSON.stringify(orderData),
      }),
    close: (orderId, options = {}) =>
      apiRequest(`/orders/${orderId}/close`, {
        method: "POST",
        body: JSON.stringify(options),
      }),
    cancel: (orderId, payload = {}) =>
      apiRequest(`/orders/${orderId}/cancel`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    assign: (orderId, assignedTo) =>
      apiRequest(`/orders/${orderId}/assign`, {
        method: "PATCH",
        body: JSON.stringify({ assigned_to: assignedTo || null }),
      }),
    passengers: {
      list: (orderId) => apiRequest(`/orders/${orderId}/passengers`),
      create: (orderId, data) =>
        apiRequest(`/orders/${orderId}/passengers`, {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (orderId, passengerId, data) =>
        apiRequest(`/orders/${orderId}/passengers/${passengerId}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (orderId, passengerId) =>
        apiRequest(`/orders/${orderId}/passengers/${passengerId}`, {
          method: "DELETE",
        }),
    },
    serviceEntities: {
      list: (orderId, serviceId) =>
        apiRequest(`/orders/${orderId}/services/${serviceId}/entities`),
      create: (orderId, serviceId, data) =>
        apiRequest(`/orders/${orderId}/services/${serviceId}/entities`, {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (orderId, serviceId, entityId, data) =>
        apiRequest(`/orders/${orderId}/services/${serviceId}/entities/${entityId}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (orderId, serviceId, entityId) =>
        apiRequest(`/orders/${orderId}/services/${serviceId}/entities/${entityId}`, {
          method: "DELETE",
        }),
    },
  },

  // Invoices endpoints
  invoices: {
    getAll: (filters = {}) => {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.payment_status)
        queryParams.append("payment_status", filters.payment_status);
      if (filters.customer_id)
        queryParams.append("customer_id", filters.customer_id);
      const query = queryParams.toString();
      return apiRequest(`/invoices${query ? `?${query}` : ""}`);
    },
    getById: (invoiceId) => apiRequest(`/invoices/${invoiceId}`),
    create: (invoiceData) =>
      apiRequest("/invoices", {
        method: "POST",
        body: JSON.stringify(invoiceData),
      }),
    update: (invoiceId, invoiceData) =>
      apiRequest(`/invoices/${invoiceId}`, {
        method: "PUT",
        body: JSON.stringify(invoiceData),
      }),
    approve: (invoiceId) =>
      apiRequest(`/invoices/${invoiceId}/approve`, {
        method: "POST",
      }),
    updateApprovalFlags: (invoiceId, flags) =>
      apiRequest(`/invoices/${invoiceId}/approval-flags`, {
        method: "PUT",
        body: JSON.stringify(flags),
      }),
    reject: (invoiceId) =>
      apiRequest(`/invoices/${invoiceId}/reject`, {
        method: "POST",
      }),
    delete: (invoiceId) =>
      apiRequest(`/invoices/${invoiceId}`, {
        method: "DELETE",
      }),
    exportPDF: async (invoiceId) => {
      const token = getAuthToken();
      const url = `${API_BASE_URL}/invoices/${invoiceId}/pdf`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        throw new Error(
          error.detail || `HTTP error! status: ${response.status}`
        );
      }
      return response;
    },
    sendEmail: (invoiceId, body = {}) =>
      apiRequest(`/invoices/${invoiceId}/send-email`, {
        method: "POST",
        body: JSON.stringify(body || {}),
      }),
  },

  // Payments endpoints
  payments: {
    getAll: (invoiceId = null) => {
      const query = invoiceId ? `?invoice_id=${invoiceId}` : "";
      return apiRequest(`/payments${query}`);
    },
    create: (paymentData) =>
      apiRequest("/payments", {
        method: "POST",
        body: JSON.stringify(paymentData),
      }),
    refund: (refundData) =>
      apiRequest("/payments/refund", {
        method: "POST",
        body: JSON.stringify(refundData),
      }),
    getRefundPreview: (invoiceId) =>
      apiRequest(`/payments/refund-preview/${invoiceId}`),
    createVendorPayment: (paymentData) =>
      apiRequest("/payments/vendor", {
        method: "POST",
        body: JSON.stringify(paymentData),
      }),
    createVendorRefund: (refundData) =>
      apiRequest("/payments/vendor-refund", {
        method: "POST",
        body: JSON.stringify(refundData),
      }),
    getVendorPayments: () => apiRequest("/payments/vendor"),
    getVendorSettlement: (vendorId) =>
      apiRequest(`/payments/vendor/settlement/${vendorId}`),
    getRefundAllocations: (filters = {}) => {
      const query = new URLSearchParams();
      if (filters.invoice_id) query.set("invoice_id", filters.invoice_id);
      if (filters.vendor_id) query.set("vendor_id", filters.vendor_id);
      const qs = query.toString();
      return apiRequest(`/payments/refund-allocations${qs ? `?${qs}` : ""}`);
    },
  },

  accounting: {
    getOpeningBalances: () => apiRequest("/accounting/opening-balances"),
    saveOpeningBalance: (data) =>
      apiRequest("/accounting/opening-balances", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    getCompanyOverview: () => apiRequest("/accounting/company-overview"),
    getBalanceSheet: (asOfDate) =>
      apiRequest(
        `/accounting/balance-sheet${asOfDate ? `?as_of_date=${encodeURIComponent(asOfDate)}` : ""}`,
      ),
    getCashFlow: (startDate, endDate) =>
      apiRequest(`/accounting/cash-flow?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`),
    createBalanceAdjustment: (data) =>
      apiRequest("/accounting/balance-adjustments", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    createInternalTransfer: (data) =>
      apiRequest("/accounting/internal-transfer", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    getLedgerActivity: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.entry_type) params.append("entry_type", filters.entry_type);
      if (filters.account_type) params.append("account_type", filters.account_type);
      const query = params.toString();
      return apiRequest(`/accounting/ledger-activity${query ? `?${query}` : ""}`);
    },
  },

  // Income endpoints
  income: {
    getAll: (filters = {}) => {
      const queryParams = new URLSearchParams();
      if (filters.start_date)
        queryParams.append("start_date", filters.start_date);
      if (filters.end_date) queryParams.append("end_date", filters.end_date);
      const query = queryParams.toString();
      return apiRequest(`/income${query ? `?${query}` : ""}`);
    },
    create: (incomeData) =>
      apiRequest("/income", {
        method: "POST",
        body: JSON.stringify(incomeData),
      }),
    update: (incomeId, incomeData) =>
      apiRequest(`/income/${incomeId}`, {
        method: "PUT",
        body: JSON.stringify(incomeData),
      }),
    delete: (incomeId) =>
      apiRequest(`/income/${incomeId}`, {
        method: "DELETE",
      }),
  },

  // Expenses endpoints
  expenses: {
    getAll: (filters = {}) => {
      const queryParams = new URLSearchParams();
      if (filters.start_date)
        queryParams.append("start_date", filters.start_date);
      if (filters.end_date) queryParams.append("end_date", filters.end_date);
      const query = queryParams.toString();
      return apiRequest(`/expenses${query ? `?${query}` : ""}`);
    },
    create: (expenseData) =>
      apiRequest("/expenses", {
        method: "POST",
        body: JSON.stringify(expenseData),
      }),
    update: (expenseId, expenseData) =>
      apiRequest(`/expenses/${expenseId}`, {
        method: "PUT",
        body: JSON.stringify(expenseData),
      }),
    delete: (expenseId) =>
      apiRequest(`/expenses/${expenseId}`, {
        method: "DELETE",
      }),
  },

  // Reports endpoints
  reports: {
    profitLoss: (filtersOrStartDate, endDate = null) => {
      const params = new URLSearchParams();
      if (typeof filtersOrStartDate === "object") {
        Object.entries(filtersOrStartDate || {}).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      } else {
        if (filtersOrStartDate) params.append("start_date", filtersOrStartDate);
        if (endDate) params.append("end_date", endDate);
      }
      return apiRequest(`/reports/profit-loss?${params.toString()}`);
    },
    collections: () => apiRequest("/reports/collections"),
    staffOptions: () => apiRequest("/reports/staff-options"),
    serviceProfitability: (startDate, endDate, salesperson) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (salesperson) params.append("salesperson", salesperson);
      return apiRequest(`/reports/service-profitability?${params.toString()}`);
    },
    salesFunnel: (startDate, endDate, assignedTo) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (assignedTo) params.append("assigned_to", assignedTo);
      return apiRequest(`/reports/sales-funnel?${params.toString()}`);
    },
    // Financial reports (GAP 2.2)
    revenueByPeriod: (groupBy, startDate, endDate) => {
      const params = new URLSearchParams();
      if (groupBy) params.append("group_by", groupBy);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/revenue-by-period?${params.toString()}`);
    },
    revenueBySalesperson: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/revenue-by-salesperson?${params.toString()}`);
    },
    revenueByServiceType: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/revenue-by-service-type?${params.toString()}`);
    },
    revenueByClient: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/revenue-by-client?${params.toString()}`);
    },
    outstandingAging: () => apiRequest("/reports/outstanding-aging"),
    paidInvoices: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/paid-invoices?${params.toString()}`);
    },
    expenseByCategory: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/expense-by-category?${params.toString()}`);
    },
    plStatement: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/pl-statement?${params.toString()}`);
    },
    soaClient: (customerId, startDate, endDate) => {
      const params = new URLSearchParams();
      if (customerId) params.append("customer_id", customerId);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/soa-client?${params.toString()}`);
    },
    soaVendor: (vendorId, startDate, endDate) => {
      const params = new URLSearchParams();
      if (vendorId) params.append("vendor_id", vendorId);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/soa-vendor?${params.toString()}`);
    },
    statement: (partyType, partyId, startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("display_format", "detailed");
      const query = params.toString();
      return apiRequest(
        `/reports/statement/${partyType}/${partyId}${query ? `?${query}` : ""}`
      );
    },
    statementSigned: (partyType, partyId, startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("display_format", "signed");
      const query = params.toString();
      return apiRequest(
        `/reports/statement/${partyType}/${partyId}${query ? `?${query}` : ""}`
      );
    },
    dailyOperations: (reportDate) =>
      apiRequest(`/reports/daily-operations${reportDate ? `?report_date=${encodeURIComponent(reportDate)}` : ""}`),
    ledgerSummary: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.customer_id) params.append("customer_id", filters.customer_id);
      if (filters.vendor_id) params.append("vendor_id", filters.vendor_id);
      if (filters.staff_id) params.append("staff_id", filters.staff_id);
      const query = params.toString();
      return apiRequest(`/reports/ledger-summary${query ? `?${query}` : ""}`);
    },
    roi: (startDate, endDate, serviceType, salesperson) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (serviceType) params.append("service_type", serviceType);
      if (salesperson) params.append("salesperson", salesperson);
      return apiRequest(`/reports/roi?${params.toString()}`);
    },
    paxByService: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/pax-by-service?${params.toString()}`);
    },
    revenuePerPax: (startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/revenue-per-pax?${params.toString()}`);
    },
    paxTrends: (groupBy, startDate, endDate) => {
      const params = new URLSearchParams();
      if (groupBy) params.append("group_by", groupBy);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/reports/pax-trends?${params.toString()}`);
    },
    exportPDF: (reportType, startDate = null, endDate = null) => {
      const queryParams = new URLSearchParams({ report_type: reportType });
      if (startDate) queryParams.append("start_date", startDate);
      if (endDate) queryParams.append("end_date", endDate);
      return apiRequest(`/reports/export/pdf?${queryParams.toString()}`);
    },
    exportExcel: (reportType, startDate = null, endDate = null) => {
      const queryParams = new URLSearchParams({ report_type: reportType });
      if (startDate) queryParams.append("start_date", startDate);
      if (endDate) queryParams.append("end_date", endDate);
      return apiRequest(`/reports/export/excel?${queryParams.toString()}`);
    },
  },

  // Settings endpoints
  settings: {
    getCompany: () => apiRequest("/settings/company"),
    getRoleAccessMatrix: () => apiRequest("/settings/role-access-matrix"),
    updateCompany: (settingsData) =>
      apiRequest("/settings/company", {
        method: "PUT",
        body: JSON.stringify(settingsData),
      }),
    getMinPrices: () => apiRequest("/settings/min-prices"),
    updateMinPrices: (prices) =>
      apiRequest("/settings/min-prices", {
        method: "PUT",
        body: JSON.stringify({ prices }),
      }),
    getInvoiceNumberFormat: () => apiRequest("/settings/invoice-number-format"),
    updateInvoiceNumberFormat: (format) =>
      apiRequest("/settings/invoice-number-format", {
        method: "PUT",
        body: JSON.stringify(format),
      }),
    getInvoiceTemplate: () => apiRequest("/settings/invoice-template"),
    updateInvoiceTemplate: (template) =>
      apiRequest("/settings/invoice-template", {
        method: "PUT",
        body: JSON.stringify(template),
      }),
    getDefaultPricingView: () => apiRequest("/settings/default-pricing-view"),
    updateDefaultPricingView: (pv) =>
      apiRequest("/settings/default-pricing-view", {
        method: "PUT",
        body: JSON.stringify(pv),
      }),
    getDailyReport: () => apiRequest("/settings/daily-report"),
    updateDailyReport: (settingsData) =>
      apiRequest("/settings/daily-report", {
        method: "PUT",
        body: JSON.stringify(settingsData),
      }),
    sendDailyReportTestEmail: (payload) =>
      apiRequest("/settings/daily-report/test-email", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getTaskTemplates: () => apiRequest("/settings/task-templates"),
    createTaskTemplate: (data) =>
      apiRequest("/settings/task-templates", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updateTaskTemplate: (id, data) =>
      apiRequest(`/settings/task-templates/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    deleteTaskTemplate: (id) =>
      apiRequest(`/settings/task-templates/${id}`, {
        method: "DELETE",
      }),
    resetCompanyData: (payload) =>
      apiRequest("/settings/danger/reset-company-data", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getResetCompanyDataPreview: () =>
      apiRequest("/settings/danger/reset-company-data-preview"),
  },

  // Dashboard endpoints
  dashboard: {
    getStats: () => apiRequest("/dashboard/stats"),
    getOpenOrders: () => apiRequest("/dashboard/open-orders"),
    getSalesFunnelSummary: () => apiRequest("/dashboard/sales-funnel-summary"),
    getPendingApprovals: () => apiRequest("/dashboard/pending-approvals"),
    getDuePayments: () => apiRequest("/dashboard/due-payments"),
    getDailyReport: (reportDate = null) =>
      apiRequest(
        `/dashboard/daily-report${
          reportDate ? `?report_date=${encodeURIComponent(reportDate)}` : ""
        }`,
      ),
    getTasks: () => apiRequest("/dashboard/tasks"),
    updateTask: (taskId, status) =>
      apiRequest(`/dashboard/tasks/${taskId}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
        showStatusPopup: false,
      }),
  },

  workflowTickets: {
    list: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.view) params.append("view", filters.view);
      if (filters.status) params.append("status", filters.status);
      if (filters.q) params.append("q", filters.q);
      if (filters.to_role) params.append("to_role", filters.to_role);
      if (filters.to_user_id) params.append("to_user_id", filters.to_user_id);
      if (filters.source_type) params.append("source_type", filters.source_type);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      const qs = params.toString();
      return apiRequest(`/workflow-tickets${qs ? `?${qs}` : ""}`);
    },
    summary: () => apiRequest("/workflow-tickets/summary"),
    summaryAdmin: () => apiRequest("/workflow-tickets/summary-admin"),
    create: (payload) =>
      apiRequest("/workflow-tickets", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    updateStatus: (id, status, comment = "") =>
      apiRequest(`/workflow-tickets/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status, comment }),
      }),
    accept: (id) =>
      apiRequest(`/workflow-tickets/${id}/accept`, {
        method: "PUT",
      }),
    complete: (id, remark) =>
      apiRequest(`/workflow-tickets/${id}/complete`, {
        method: "PUT",
        body: JSON.stringify({ remark }),
      }),
    assign: (id, payload) =>
      apiRequest(`/workflow-tickets/${id}/assign`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    addComment: (id, comment) =>
      apiRequest(`/workflow-tickets/${id}/comments`, {
        method: "POST",
        body: JSON.stringify({ comment }),
      }),
    events: (id) => apiRequest(`/workflow-tickets/${id}/events`),
  },

  notifications: {
    list: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.unreadOnly) params.append("unread", "1");
      const qs = params.toString();
      return apiRequest(`/notifications${qs ? `?${qs}` : ""}`);
    },
    summary: () => apiRequest("/notifications/summary"),
    markRead: (id) =>
      apiRequest(`/notifications/${id}/read`, {
        method: "PUT",
        showStatusPopup: false,
      }),
    markAllRead: () =>
      apiRequest("/notifications/read-all", {
        method: "PUT",
        showStatusPopup: false,
      }),
  },

  // Subscription endpoints
  subscriptions: {
    getPackages: () => apiRequest("/subscriptions/packages"),
    getPackage: (packageId) =>
      apiRequest(`/subscriptions/packages/${packageId}`),
    register: (userData) =>
      apiRequest("/subscriptions/register", {
        method: "POST",
        body: JSON.stringify(userData),
      }),
    create: (packageId) =>
      apiRequest("/subscriptions/create", {
        method: "POST",
        body: JSON.stringify({ package_id: packageId }),
      }),
    getCurrent: () => apiRequest("/subscriptions/current"),
    getMySubscriptions: () => apiRequest("/subscriptions/my-subscriptions"),
    changePlan: (packageId) =>
      apiRequest("/subscriptions/change-plan", {
        method: "PUT",
        body: JSON.stringify({ package_id: packageId }),
      }),
    createPaymentIntent: (subscriptionId) =>
      apiRequest("/subscriptions/create-payment-intent", {
        method: "POST",
        body: JSON.stringify({ subscription_id: subscriptionId }),
      }),
    confirmPayment: (subscriptionId, paymentIntentId) =>
      apiRequest("/subscriptions/confirm-payment", {
        method: "POST",
        body: JSON.stringify({
          subscription_id: subscriptionId,
          payment_intent_id: paymentIntentId,
        }),
      }),
  },

  // Super Admin endpoints
  superAdmin: {
    getUsers: () => apiRequest("/super-admin/users"),
    getUserStats: () => apiRequest("/super-admin/users/stats"),
    getSubscriptions: () => apiRequest("/super-admin/subscriptions"),
    getSubscriptionStats: () => apiRequest("/super-admin/subscriptions/stats"),
    getSaasAnalytics: () => apiRequest("/super-admin/saas-analytics"),
    getCompanies: () => apiRequest("/super-admin/companies"),
    createCompany: (data) =>
      apiRequest("/super-admin/companies", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updateCompany: (id, data) =>
      apiRequest(`/super-admin/companies/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    updateCompanySubscription: (companyId, data) =>
      apiRequest(`/super-admin/companies/${companyId}/subscription`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onboardCompany: (data) =>
      apiRequest("/super-admin/onboard-company", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    getRevenue: (startDate = null, endDate = null) => {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      return apiRequest(`/super-admin/revenue?${params.toString()}`);
    },
    getAuditLogs: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.action) params.append("action", filters.action);
      if (filters.entity_type)
        params.append("entity_type", filters.entity_type);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.offset) params.append("offset", filters.offset);
      return apiRequest(`/super-admin/audit-logs?${params.toString()}`);
    },
    getAuditLogStats: () => apiRequest("/super-admin/audit-logs/stats"),
    getOverview: () => apiRequest("/super-admin/overview"),
    changePassword: (oldPassword, newPassword) =>
      apiRequest("/super-admin/change-password", {
        method: "POST",
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      }),
    // Package Management
    getPackages: () => apiRequest("/super-admin/packages"),
    createPackage: (packageData) =>
      apiRequest("/super-admin/packages", {
        method: "POST",
        body: JSON.stringify(packageData),
      }),
    updatePackage: (packageId, packageData) =>
      apiRequest(`/super-admin/packages/${packageId}`, {
        method: "PUT",
        body: JSON.stringify(packageData),
      }),
    deletePackage: (packageId) =>
      apiRequest(`/super-admin/packages/${packageId}`, {
        method: "DELETE",
      }),
    // Admin Management
    createAdmin: (adminData) =>
      apiRequest("/super-admin/create-admin", {
        method: "POST",
        body: JSON.stringify(adminData),
      }),
    updateAdminStatus: (userId, isActive) =>
      apiRequest(`/super-admin/admins/${userId}/status`, {
        method: "PUT",
        body: JSON.stringify({ is_active: isActive }),
      }),
    extendSubscription: (subscriptionId, months = null, days = null) =>
      apiRequest(`/super-admin/subscriptions/${subscriptionId}/extend`, {
        method: "PUT",
        body: JSON.stringify({ months, days }),
      }),
  },
};

export default api;
